import { OpenAiHttpError, readOpenAiHttpError } from "../../supabase/functions/_shared/openAiHttpError";
import type { ProfessorSelection } from '@/lib/language/professor';
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase, supabaseUrl } from "@/integrations/supabase/client";
import { mirrorAuthIdentity } from "@/lib/mirrorAuthSession";
import {
  advanceMirrorLiveCurrentTurn, createMirrorLiveCurrentTurnState, invalidateMirrorLiveCurrentTurn,
  isMirrorLiveCurrentTurnCallCurrent, LIVE_TOOL_TRANSCRIPT_WAIT_MS, LIVE_TOOL_REQUEST_TIMEOUT_MS,
  type MirrorLiveCurrentTurnCall, type MirrorLiveCurrentTurnState, type MirrorLiveToolHandoff,
} from "@/lib/mirrorLiveCurrentTurn";
import { readMirrorChannelOutcome } from "../../supabase/functions/_shared/mirrorChannelOutcome";
import { mirrorAppHandoffResponse, mirrorChannelCopy } from "../../supabase/functions/_shared/whatsappMirrorCopy";
export type { MirrorLiveToolHandoff } from "@/lib/mirrorLiveCurrentTurn";
import { parsePaymentErrorFromInvokeError, type PaymentError } from "@/lib/quotaError";
import { acquireVoiceChatAudioSession, type NativeAudioSessionInfo, type NativeVoiceChatAudioSession } from "@/lib/nativeAudioSession";
import { appAudioFocus, type AppAudioLease } from "@/lib/appAudioFocus";
import { reportLiveAudio } from "@/lib/liveAudioTelemetry";
import type { MirrorPresenceState } from "@/components/mirror/MirrorPresenceConfig";

// ─────────────────────────────────────────────────────────────────────────────
// useMirrorLive — o LIVE do Espelho por OpenAI Realtime (LOTE 3, 05/09/2026).
//
// A ORDEM importa, e é esta:
//   a) 🔴 MICROFONE ANTES DA CHAVE. getUserMedia primeiro. O mirror-live-token
//      pré-cobra 30 s da cota no ato — pedir a chave antes cobraria de um
//      usuário que ainda vai negar o microfone: 10% do teto mensal do Premium
//      por uma caixa de diálogo.
//   b) POST mirror-live-token (portão + pré-cobrança + chave efêmera) →
//      RTCPeerConnection + addTrack(mic) + canal "oai-events" → SDP offer →
//      POST /v1/realtime/calls com o client_secret → answer.
//   c) call_id (header Location do handshake) vai no PRIMEIRO tique — é o que
//      permite ao servidor derrubar a chamada quando a cota acaba.
//   d) tique a cada tick_seconds (o valor vem do token, não daqui);
//      continue:false → derruba a conexão e mostra o fim.
//   e) { end: true } no encerramento limpo: unmount, enabled → false (troca
//      de superfície), visibilitychange hidden, pagehide/beforeunload.
//   f) session_ttl_seconds (relativo, LOTE 4) ou, na falta dele,
//      session_expires_at: encerra quando chegar — SÓ se for um prazo real e
//      futuro (sessionTtlDelayMs / sessionExpiryDelayMs). É o fim da SESSÃO,
//      nunca a validade da chave; valor no passado não encerra nada, grita
//      (LOTE 3.1). O relativo tira o relógio do aparelho da conta.
//   g) secondsRemaining para a UI (contagem local entre tiques).
//
// LOTE 4 (05/09/2026):
//   · a BOCA: o analyser remoto só produz dado se o AudioContext estiver
//     "running". O grafo do LOTE 4 terminava num destino (source → analyser
//     → GainNode(0) → destination) — DESFEITO no LOTE 4.4, abaixo: hoje
//     termina no analyser. O que fica: estado conferido depois do resume()
//     (grita se não estiver running) e um console.info único ~2 s depois de
//     conectar com a média do getByteFrequencyData — zero com voz tocando é
//     diagnóstico em uma linha.
//   · aba escondida NÃO encerra na hora: espera hidden_grace_seconds (vem
//     do token; default = tick_seconds). Voltar dentro da janela cancela.
//     Dez trocas de app não podem custar dez pré-cobranças. LOTE 4.1: o
//     microfone é silenciado (track.enabled=false) durante a janela —
//     privacidade — e a cobrança continua.
//   · LOTE 4.1: o analyser lê uma trilha CLONADA (o <audio> consome a
//     original e o Web Audio recebia silêncio), e a sonda amostra 15 s.
//   · ended_detail junto do end:true — POR QUE o cliente encerrou (button,
//     timer, hidden, tick_failed, disconnected, handshake_error,
//     navigation). Diagnóstico, não dinheiro.
//   · 503 { reason: 'live_disabled' } do token = kill switch → endReason
//     'disabled' (copy própria), nunca 'error'.
//   · o idioma da UI vai no corpo do token (language), como no mirror-chat.
//
// LOTE 4.4 (06/09/2026) — os estalos na voz do LIVE (som metálico, cortando,
// intermitente, relatado depois do 4.1). O grafo do 4.1 era
// `clone → source → analyser → GainNode(0) → destination` no AudioContext
// COMPARTILHADO do desbloqueio iOS (criado no primeiro toque do app, com o
// sample rate default DAQUELE momento), em paralelo ao <audio> que toca a
// trilha original. O que muda:
//   · a SONDA DE TAXAS: uma linha no attach com ctx.sampleRate, o sampleRate
//     que a trilha remota declara (getSettings — o Chromium costuma não
//     declarar: "n/a"), o do microfone, baseLatency, state e a origem do
//     contexto. Taxas diferentes = resampling contínuo no analyser: meio
//     diagnóstico de graça, sem experimento manual.
//   · o grafo TERMINA NO ANALYSER: sem GainNode(0), sem destination. O
//     AnalyserNode processa mesmo com a saída solta (é "automatic pull node"
//     no Blink e no WebKit — a regra 1 do LOTE 4 era cinto e suspensório, e
//     o suspensório custava uma segunda rota até a saída). A sonda de 15 s
//     é o critério: pico > 0 com falou=true, o ganho era desnecessário.
//   · AudioContext PRÓPRIO por sessão (createLiveAudioContext), criado
//     DENTRO do gesto que pede o microfone (iOS), com a taxa da trilha do
//     MICROFONE quando ela a declara — a trilha remota só existe no ontrack,
//     fora do gesto, e no iOS mic e reprodução vivem na mesma sessão de
//     áudio, então a taxa do mic é a da sessão. Fechado no teardown junto
//     com o clone (e nos retornos antecipados do start: token recusado,
//     shape errado, cancelamento). O compartilhado (iosAudioUnlock) não é
//     mais tocado por este hook: ele nasceu para outra coisa, com outra
//     taxa, e é usado por outras superfícies.
//   · SE ISSO NÃO BASTAR — saída sem Web Audio nenhum, NÃO implementada:
//     a boca pelos eventos do canal oai-events (output_audio_buffer.started/
//     stopped + response.output_audio_transcript.delta). O LiveMouthDriver
//     já recebe a amplitude por um AnalyserLike ({ frequencyBinCount,
//     getByteFrequencyData }); um adaptador que preencha esse buffer a
//     partir dos eventos (nível cheio entre started e stopped, com um
//     decaimento a cada delta de transcript) entra no lugar do analyser sem
//     driver novo — e some o clone, o source e o contexto. A condição que
//     dispara: a sonda de taxas mostrando ctx.sampleRate == trilha (sem
//     resampling), pico > 0 com falou=true (o analyser funcionando) e o
//     estalo AINDA presente — aí o segundo consumidor da trilha é a causa,
//     não a taxa nem a rota de saída.
//
// LOTE 5 (06/09/2026) — a sessão de áudio NATIVA do iPhone e a sonda que sai
// do aparelho:
//   · setVoiceChatMode() ANTES do getUserMedia, no mesmo gesto: põe a
//     AVAudioSession em .playAndRecord + .voiceChat (AEC do sistema, rota
//     duplex, defaultToSpeaker) em vez do que o WKWebView escolher sozinho.
//     setPlaybackMode() no teardown E em todo retorno antecipado do start —
//     senão a sessão fica em playAndRecord depois da conversa e o app inteiro
//     herda isso. Fora do iOS as duas são no-op (o plugin não existe lá).
//     ⚠️ O plugin SÓ passou a existir no binário neste mesmo lote: até aqui o
//     AudioSessionPlugin.swift não estava no Compile Sources do target App
//     (Sentry VYV-FRONTEND-50, 257 eventos / 140 usuários). Ou seja: este
//     caminho nunca rodou em produção, e o que ele conserta é hipótese até o
//     primeiro build.
//   · a sonda vai para o SENTRY (reportLiveAudio), um evento nível info por
//     sessão, com as QUATRO taxas (contexto, trilha remota, mic, sessão
//     nativa), as latências, a rota de saída e o resumo de 15 s. No iPhone
//     ninguém abre o DevTools: sem isso o aceite depende do ouvido de uma
//     pessoa num aparelho só.
//
// O que este hook NUNCA faz: mandar duração ao servidor (o delta é do
// servidor), tocar na chave mestra da OpenAI (só a chave efêmera ek_… passa
// por aqui) ou decidir plano (isso é a constante de acesso em @/lib/liveAccess).
//
// Testado em src/test/mirrorLiveClient.test.tsx com RTCPeerConnection,
// getUserMedia e fetch falsos que gravam a ORDEM das chamadas.
// ─────────────────────────────────────────────────────────────────────────────

export const OPENAI_REALTIME_CALLS_URL = "https://api.openai.com/v1/realtime/calls";
export const OAI_EVENTS_CHANNEL = "oai-events";
export const TOKEN_FN = "mirror-live-token";
export const TICK_FN = "mirror-live-tick";
/** Tiques seguidos sem resposta antes de encerrar por conta própria. */
export const TICK_FAILURES_BEFORE_END = 3;
/**
 * Tolerância para `connectionState === "disconnected"` (review Codex P2):
 * troca Wi-Fi/4G e ICE restart passam por "disconnected" e voltam. Só
 * "failed"/"closed" são terminais na hora; "disconnected" espera isto.
 */
export const DISCONNECT_GRACE_MS = 10_000;

/** Folga mínima para armar o timer de fim de sessão: abaixo disto não é prazo, é bug. */
export const SESSION_EXPIRY_MIN_LEAD_MS = 1000;
/**
 * Teto de plausibilidade: acima disto não é prazo, é unidade errada — e o
 * setTimeout do navegador, acima de 2^31−1 ms (~24,8 dias), dispara NA HORA.
 * Seria a mesma morte em 2 s, pelo outro lado.
 */
export const SESSION_EXPIRY_MAX_LEAD_MS = 24 * 3600 * 1000;

/** LOTE 4 — ~2 s depois de conectar, a PRIMEIRA leitura do analyser vai para o console.info. */
export const ANALYSER_PROBE_DELAY_MS = 2000;
/**
 * LOTE 4.1 — a sonda é o teste de aceitação da boca, então uma leitura num
 * instante não basta (2 s depois de conectar o modelo pode nem ter começado a
 * falar: média 0 legítima). Amostra a cada SAMPLE_MS por WINDOW_MS e resume
 * UMA vez: pico, média, amostras com sinal e se houve fala do modelo
 * (output_audio_buffer.started). Pico > 0 com falou=true é o critério.
 */
export const ANALYSER_PROBE_WINDOW_MS = 15_000;
export const ANALYSER_PROBE_SAMPLE_MS = 250;

/**
 * LOTE 4.4 — o sample rate que uma trilha DECLARA (getSettings().sampleRate),
 * ou null. A trilha do microfone declara a taxa da sessão de áudio do
 * aparelho; a trilha remota do WebRTC costuma não declarar (o Chromium devolve
 * um objeto quase vazio) — e "n/a" na sonda é informação, não falha.
 */
export function trackSampleRate(track: MediaStreamTrack | null | undefined): number | null {
  if (!track || typeof track.getSettings !== "function") return null;
  try {
    const rate = (track.getSettings() as { sampleRate?: unknown }).sampleRate;
    return typeof rate === "number" && Number.isFinite(rate) && rate > 0 ? rate : null;
  } catch (_e) {
    return null;
  }
}

/**
 * LOTE 4.4 — o AudioContext PRÓPRIO de uma sessão de LIVE. Criado pelo start()
 * DENTRO do gesto que pediu o microfone (o iOS exige gesto para um contexto
 * novo tocar), com `{ sampleRate }` quando a trilha do microfone declara a
 * taxa — no iOS, mic e reprodução vivem na mesma sessão de áudio, então essa
 * é a taxa que evita o resampling contínuo do analyser. Navegador que recusa a
 * opção (webkitAudioContext antigo, taxa fora da faixa) cai para o default e
 * avisa; sem Web Audio nenhum, null e grita — o LIVE fala pelo <audio> do
 * mesmo jeito, só a boca não mexe. Quem cria fecha: silenceLocal / os
 * retornos antecipados do start.
 */
export function createLiveAudioContext(sampleRate: number | null): AudioContext | null {
  const w = window as unknown as { AudioContext?: typeof AudioContext; webkitAudioContext?: typeof AudioContext };
  const Ctor = w.AudioContext || w.webkitAudioContext;
  if (!Ctor) {
    console.error("[useMirrorLive] AudioContext indisponível — o LIVE fala pelo <audio>, mas a boca não vai mexer");
    return null;
  }
  if (sampleRate !== null) {
    try {
      return new Ctor({ sampleRate });
    } catch (e) {
      console.warn("[useMirrorLive] AudioContext({ sampleRate: " + String(sampleRate) + " }) recusado — criando com a taxa default", e);
    }
  }
  try {
    return new Ctor();
  } catch (e) {
    console.error("[useMirrorLive] AudioContext não pôde ser criado — a boca não vai mexer", e);
    return null;
  }
}

export type MirrorLiveStatus = "idle" | "requesting_mic" | "connecting" | "live" | "ended";
export type MirrorLiveEndReason =
  | "client"
  | "quota"
  | "no_quota"
  | "premium_required"
  | "expired"
  | "error"
  | "mic_denied"
  | "disconnected"
  /** LOTE 4 — kill switch (503 live_disabled): copy própria, não "erro". */
  | "disabled";

/**
 * LOTE 4 — o que o cliente conta ao servidor junto do end:true. Espelha
 * LIVE_ENDED_DETAILS (_shared/mirrorLive.ts) e o CHECK da migration
 * 20260905150000. Diagnóstico, não dinheiro: o servidor decide reason e
 * cobrança sozinho.
 */
export type LiveEndedDetail =
  | "button"
  | "timer"
  | "hidden"
  | "tick_failed"
  | "mic_denied"
  | "disconnected"
  | "handshake_error"
  | "navigation";

export interface MirrorLiveTokenResponse {
  language_professor_protocol?: number;
  session_id: string;
  client_secret: string;
  /** Opaque server authority for the negotiated, current-audio-turn bridge. */
  live_tool_lease?: string;
  live_tool_run_nonce?: string;
  /**
   * ISO — validade da CHAVE efêmera (client_secret), só para o handshake.
   * NÃO é o fim da sessão: o hook nem lê. Armar timer com isto mataria toda
   * conversa aos 60 s (voice.live_key_ttl_seconds).
   */
  expires_at: string;
  /**
   * ISO — fim REAL da sessão na OpenAI, ou null = sem prazo (o normal hoje:
   * no client_secrets a sessão ainda não existe). DEPRECADO desde o LOTE 4:
   * só é lido quando session_ttl_seconds não vem (function antiga). Passa
   * por sessionExpiryDelayMs() antes de virar timer.
   */
  session_expires_at: string | null;
  /**
   * LOTE 4 — o MESMO prazo, RELATIVO em segundos (formato de tick_seconds).
   * Preferido: não depende do relógio do aparelho. null = sem prazo.
   */
  session_ttl_seconds?: number | null;
  seconds_remaining: number;
  model: string;
  tick_seconds: number;
  precharge_seconds: number;
  /** LOTE 4 — tolerância (s) da aba escondida. Ausente (function antiga) = tick_seconds. */
  hidden_grace_seconds?: number;
}

export interface MirrorLiveTickResponse {
  continue: boolean;
  seconds_remaining: number;
  ended_reason: string | null;
  seconds_charged: number;
  delta_seconds: number;
  /** LOTE 4.2 — a transcrição mandada neste tique foi gravada? Ausente (function antiga) = nada a reenviar. */
  transcripts_ok?: boolean;
  transcripts_saved?: number;
}

/**
 * LOTE 4.2 — uma fala transcrita, capturada do canal oai-events e mandada ao
 * servidor pegando carona no tique. item_id é a chave de idempotência (o
 * servidor prefixa com o id da sessão). TEXTO, nunca duração; o servidor
 * valida o role e descarta o resto.
 */
export interface LiveTranscript {
  item_id: string;
  role: "user" | "assistant";
  text: string;
}
/** Falas em memória esperando um tique. Acima disto a mais antiga cai, com aviso. */
export const TRANSCRIPT_BUFFER_MAX = 200;
/** Quantas vão por tique (o servidor descarta o excedente). */
export const TRANSCRIPTS_PER_TICK = 100;
export const TRANSCRIPT_MAX_CHARS = 4000;
/**
 * Review Codex P1 (LOTE 4.2) — a transcrição do usuário é ASSÍNCRONA: quem
 * encerra logo depois de falar derrubava o canal de dados antes de o
 * `…input_audio_transcription.completed` chegar, e a última fala sumia para
 * sempre. finish() silencia a mídia na hora (o que o usuário vê) e espera até
 * isto pelas falas comprometidas ainda sem texto antes de fechar o transporte.
 */
export const TRANSCRIPT_DRAIN_MS = 2000;
/**
 * Review Codex P2 (LOTE 4.2) — `fetch(keepalive)` tem teto de ~64 KiB de
 * corpo em voo; acima disso o navegador rejeita ANTES de mandar, e o
 * `end:true` do pagehide se perde junto. O rabo do unload cabe nisto.
 */
export const KEEPALIVE_BODY_MAX_BYTES = 48 * 1024;

/**
 * O maior prefixo CRONOLÓGICO de `items` cujo JSON cabe em `maxBytes`
 * (UTF-8). Prefixo, não sufixo: a ordem do que foi dito vale mais que o
 * pedaço mais recente, e no caso normal tudo cabe.
 */
export function fitTranscriptsInBytes(items: LiveTranscript[], maxBytes: number): LiveTranscript[] {
  const enc = new TextEncoder();
  let bytes = 2; // "[" + "]"
  const out: LiveTranscript[] = [];
  for (const it of items) {
    const size = enc.encode(JSON.stringify(it)).length + (out.length > 0 ? 1 : 0);
    if (bytes + size > maxBytes) break;
    bytes += size;
    out.push(it);
  }
  return out;
}

export interface UseMirrorLiveOptions {
  /** Strict identifiers only. Server owns all teaching instructions and content. */
  languageProfessor?: ProfessorSelection;
  /** false = hook inerte (e encerra uma sessão viva, com end:true). */
  enabled: boolean;
  /** 402 do token (Free fora da grade / Premium sem minutos) — roteador de paywall. */
  onQuotaExceeded?: (err: PaymentError) => void;
  /**
   * LOTE 4 — idioma da UI (o `lang` do LanguageProvider), mandado no corpo
   * do token como o MirrorChat manda ao mirror-chat. O servidor resolve o
   * idioma das instructions por ele; sem ele cai no perfil e avisa.
   */
  language?: string;
  /** Injected by the authenticated production wrapper; the transport core also
   * supports callers that own their authentication outside this hook. */
  authority?: MirrorLiveAuthority;
  /** Creates/returns the current owned app conversation; never sends a command. */
  prepareToolSession?: () => Promise<string | null>;
  /** Receives an inert app draft only; the user decides whether to send it. */
  onToolHandoff?: (handoff: MirrorLiveToolHandoff) => void;
}

export interface MirrorLiveAuthorityLease {
  readonly ownerId: string;
  readonly authSessionId: string;
  readonly originalToken: string;
  isCurrent(): boolean;
  token(): string | null;
  acceptsSession(session: unknown): boolean;
}

export interface MirrorLiveAuthority {
  acquire(): MirrorLiveAuthorityLease | null;
  subscribeInvalidation(cancel: () => void): () => void;
}

interface LiveToolJob {
  call: MirrorLiveCurrentTurnCall;
  controller: AbortController;
  wake: (() => void) | null;
  timer: number | null;
}
interface LiveToolRuntime {
  lease: string;
  runNonce: string;
  language: string | undefined;
  prepareSession: () => Promise<string | null>;
  onHandoff: ((handoff: MirrorLiveToolHandoff) => void) | undefined;
  state: MirrorLiveCurrentTurnState;
  acknowledged: Set<string>;
  jobs: Set<LiveToolJob>;
}
interface LiveRun {
  generation: number;
  authority: MirrorLiveAuthorityLease | null;
  invalidated: boolean;
  ending: boolean;
  sessionId: string | null;
  accessToken: string | null;
  callId: string | null;
  callIdSent: boolean;
  serverClosed: boolean;
  handshakeInFlight: boolean;
  deferredEnd: { detail?: LiveEndedDetail } | null;
  finalTickPending: boolean;
  transcripts: LiveTranscript[];
  pendingUserItems: Set<string>;
  responseActive: boolean;
  drainResolve: (() => void) | null;
  mic: MediaStream | null;
  pc: RTCPeerConnection | null;
  dc: RTCDataChannel | null;
  context: AudioContext | null;
  nativeAudioLease: NativeVoiceChatAudioSession | null;
  focusLease: AppAudioLease | null;
  audio: HTMLAudioElement | null;
  tools: LiveToolRuntime | null;
}

export interface UseMirrorLiveResult {
  status: MirrorLiveStatus;
  presence: MirrorPresenceState;
  endReason: MirrorLiveEndReason | null;
  /** Segundos restantes no mês, contados localmente entre tiques. null = ainda não sabemos. */
  secondsRemaining: number | null;
  sessionExpiresAt: string | null;
  micStream: MediaStream | null;
  remoteStream: MediaStream | null;
  /** Analyser da voz remota — boca do avatar (caminho de amplitude do AvatarCore). */
  remoteAnalyser: AnalyserNode | null;
  isMuted: boolean;
  start: () => Promise<void>;
  end: () => Promise<void>;
  setMuted: (muted: boolean) => void;
}

/**
 * call_id do header Location do handshake: "/v1/realtime/calls/rtc_x",
 * "https://api.openai.com/v1/realtime/calls/rtc_x" ou só "rtc_x".
 */
export function extractCallId(location: string | null | undefined): string | null {
  if (typeof location !== "string") return null;
  const v = location.trim();
  if (!v) return null;
  const m = /\/realtime\/calls\/([A-Za-z0-9_-]+)\/?(?:[?#].*)?$/.exec(v);
  if (m) return m[1];
  return /^[A-Za-z0-9_-]{6,128}$/.test(v) ? v : null;
}

/**
 * Presença a partir dos eventos do canal "oai-events". Só os que mudam o
 * que o usuário vê: fala do usuário (server VAD), geração, áudio de saída
 * (output_audio_buffer.* são os eventos do transporte WebRTC). null = ignora.
 */
export function presenceFromEvent(type: unknown): MirrorPresenceState | null {
  switch (type) {
    case "input_audio_buffer.speech_started":
      return "listening";
    case "input_audio_buffer.speech_stopped":
    case "response.created":
      return "thinking";
    case "output_audio_buffer.started":
      return "speaking";
    case "output_audio_buffer.stopped":
    case "output_audio_buffer.cleared":
      return "idle";
    default:
      return null;
  }
}

/**
 * LOTE 4.2 — os DOIS eventos do canal oai-events que carregam texto dito:
 *   conversation.item.input_audio_transcription.completed → 'user'
 *     (só existe com audio.input.transcription ligado no client_secrets)
 *   response.output_audio_transcript.done → 'assistant' (nome GA; o nome
 *     beta response.audio_transcript.done é aceito para não depender da
 *     versão que a OpenAI serve à chamada)
 * Vazio, sem item_id ou qualquer outro evento = null. content_index > 0
 * entra na chave: um item com mais de uma parte de áudio não pode colapsar
 * numa linha só.
 */
export function transcriptFromEvent(
  msg: { type?: unknown; item_id?: unknown; transcript?: unknown; content_index?: unknown },
): LiveTranscript | null {
  const role: LiveTranscript["role"] | null =
    msg.type === "conversation.item.input_audio_transcription.completed"
      ? "user"
      : msg.type === "response.output_audio_transcript.done" || msg.type === "response.audio_transcript.done"
        ? "assistant"
        : null;
  if (!role) return null;
  const itemId = typeof msg.item_id === "string" ? msg.item_id.trim() : "";
  if (!itemId) return null;
  const text = typeof msg.transcript === "string" ? msg.transcript.trim() : "";
  if (!text) return null;
  const part = typeof msg.content_index === "number" && msg.content_index > 0 ? "#" + String(msg.content_index) : "";
  return { item_id: itemId + part, role, text: text.length > TRANSCRIPT_MAX_CHARS ? text.slice(0, TRANSCRIPT_MAX_CHARS) : text };
}

/** ended_reason do servidor → motivo exibido. Desconhecido = queda. */
export function endReasonFromTick(endedReason: unknown): MirrorLiveEndReason {
  if (endedReason === "quota") return "quota";
  if (endedReason === "expired") return "expired";
  if (endedReason === "client") return "client";
  if (endedReason === "error") return "error";
  return "disconnected";
}

/**
 * Quanto falta para o fim REAL da sessão, em ms — ou null = NÃO armar timer.
 *
 * 05/09/2026, produção: o servidor mandou session_expires_at =
 * "1970-01-01T00:00:00Z" (eco do session.expires_at = 0 da OpenAI), o piso
 * em zero que ficava no start() (max(0, ms)) leu "já passou" como "expira
 * agora", e cinco conversas reais morreram em 1–5 s como "expired" — com
 * 270 s de cota sobrando. Um prazo no passado é bug de SERVIDOR: tem que
 * gritar (console.error vira evento no Sentry, §11), não matar a sessão.
 * Daí as três saídas nulas com log e a exigência de folga (> 1 s).
 */
export function sessionExpiryDelayMs(iso: string | null | undefined, nowMs: number): number | null {
  if (iso === null || iso === undefined || iso === "") return null;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) {
    console.error("[useMirrorLive] session_expires_at ilegível — timer de fim desligado, sessão segue: " + JSON.stringify(iso));
    return null;
  }
  const ms = at - nowMs;
  if (ms <= SESSION_EXPIRY_MIN_LEAD_MS) {
    console.error(
      "[useMirrorLive] session_expires_at no passado (ou sem folga de 1 s) — bug de servidor; timer de fim desligado, sessão segue: " +
        iso + " (" + String(ms) + " ms vs now=" + new Date(nowMs).toISOString() + ")",
    );
    return null;
  }
  if (ms > SESSION_EXPIRY_MAX_LEAD_MS) {
    console.error(
      "[useMirrorLive] session_expires_at implausível (unidade errada?) — timer de fim desligado, sessão segue: " +
        iso + " (+" + String(ms) + " ms)",
    );
    return null;
  }
  return ms;
}

/**
 * LOTE 4 — o prazo RELATIVO (session_ttl_seconds) vale mais que o ISO: não
 * passa pelo relógio do aparelho (um celular 5 min adiantado lia um ISO
 * legítimo como "já passou"). As mesmas guardas do ISO: folga > 1 s e teto.
 * null/undefined = sem prazo; lixo = grita e não arma.
 */
export function sessionTtlDelayMs(ttlSeconds: unknown): number | null {
  if (ttlSeconds === null || ttlSeconds === undefined) return null;
  if (typeof ttlSeconds !== "number" || !Number.isFinite(ttlSeconds)) {
    console.error("[useMirrorLive] session_ttl_seconds ilegível — timer de fim desligado, sessão segue: " + JSON.stringify(ttlSeconds));
    return null;
  }
  const ms = ttlSeconds * 1000;
  if (ms <= SESSION_EXPIRY_MIN_LEAD_MS) {
    console.error("[useMirrorLive] session_ttl_seconds sem folga — timer de fim desligado, sessão segue: " + String(ttlSeconds));
    return null;
  }
  if (ms > SESSION_EXPIRY_MAX_LEAD_MS) {
    console.error("[useMirrorLive] session_ttl_seconds implausível — timer de fim desligado, sessão segue: " + String(ttlSeconds));
    return null;
  }
  return ms;
}

/**
 * LOTE 4 — kill switch: o token responde 503 { reason: 'live_disabled' }.
 * true = desligado por config (agent_config voice.live_enabled), nada foi
 * cobrado. Qualquer outro erro → false (segue o caminho de 'error').
 */
export async function isLiveDisabledError(error: unknown): Promise<boolean> {
  const ctx = (error as { context?: unknown } | null | undefined)?.context;
  if (!ctx || typeof ctx !== "object") return false;
  const res = ctx as { status?: number; json?: () => Promise<unknown>; clone?: () => { json: () => Promise<unknown> } };
  if (res.status !== 503) return false;
  try {
    const src = typeof res.clone === "function" ? res.clone() : res;
    const body = typeof src.json === "function" ? await src.json() : null;
    return !!body && typeof body === "object" && (body as { reason?: unknown }).reason === "live_disabled";
  } catch (_e) {
    return false;
  }
}

async function isProfessorPremiumRequiredError(error: unknown): Promise<boolean> {
  const context = (error as { context?: Response } | null)?.context;
  if (context?.status !== 403) return false;
  try {
    const body = await (typeof context.clone === "function" ? context.clone() : context).json();
    return body?.error === "language_professor_premium_required";
  } catch { return false; }
}

function isTokenResponse(v: unknown): v is MirrorLiveTokenResponse {
  if (!v || typeof v !== "object") return false;
  const o = v as Record<string, unknown>;
  return typeof o.session_id === "string" && typeof o.client_secret === "string" &&
    typeof o.tick_seconds === "number" && typeof o.seconds_remaining === "number";
}

function stopTracks(stream: MediaStream | null): void {
  if (!stream) return;
  try {
    stream.getTracks().forEach((t) => t.stop());
  } catch (e) {
    console.warn("[useMirrorLive] stop tracks", e);
  }
}

export function useMirrorLive(opts: UseMirrorLiveOptions): UseMirrorLiveResult {
  const { enabled, onQuotaExceeded, language } = opts;

  const runRef = useRef<LiveRun | null>(null);
  const isActiveRun = useCallback((run: LiveRun) => runRef.current === run &&
    !run.invalidated && (!run.authority || run.authority.isCurrent()), []);

  const [status, setStatusState] = useState<MirrorLiveStatus>("idle");
  const [presence, setPresence] = useState<MirrorPresenceState>("idle");
  const [endReason, setEndReason] = useState<MirrorLiveEndReason | null>(null);
  const [secondsRemaining, setSecondsRemaining] = useState<number | null>(null);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<string | null>(null);
  const [micStream, setMicStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [remoteAnalyser, setRemoteAnalyser] = useState<AnalyserNode | null>(null);
  const [isMuted, setIsMutedState] = useState(false);

  // Espelhos síncronos (§11): handlers de evento e timers leem daqui, nunca
  // de estado capturado.
  const statusRef = useRef<MirrorLiveStatus>("idle");
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);
  const micRef = useRef<MediaStream | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const remoteAudioTrackRef = useRef<MediaStreamTrack | null>(null);
  const remoteAttachmentGenerationRef = useRef(0);
  const analyserSrcRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const tickTimerRef = useRef<number | null>(null);
  const expiryTimerRef = useRef<number | null>(null);
  const tickFailuresRef = useRef(0);
  const remainingRef = useRef<{ value: number; atMs: number } | null>(null);
  const mutedRef = useRef(false);
  // Geração do start() em voo (review Codex P1): finish() incrementa; um
  // start() cujo número ficou para trás foi cancelado e devolve o que
  // obteve depois disso (mic, sessão cobrada) em vez de seguir.
  const genRef = useRef(0);
  const disconnectTimerRef = useRef<number | null>(null);
  const onQuotaRef = useRef(onQuotaExceeded);
  onQuotaRef.current = onQuotaExceeded;
  const languageRef = useRef(language);
  languageRef.current = language;
  const professorRef = useRef(opts.languageProfessor);
  professorRef.current = opts.languageProfessor;
  // LOTE 4 — a boca: o analyser e a sonda. LOTE 4.4 — o AudioContext PRÓPRIO
  // da sessão (createLiveAudioContext), criado no gesto e fechado no
  // teardown; o GainNode(0) → destination do 4.1 não existe mais.
  const audioCtxRef = useRef<AudioContext | null>(null);
  // LOTE 5 — o que a AVAudioSession negociou no setVoiceChatMode (iOS).
  // null fora do iOS, ou se a ponte falhar: a sonda mostra n/a e segue.
  const nativeAudioRef = useRef<NativeAudioSessionInfo | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const analyserProbeTimerRef = useRef<number | null>(null);
  const analyserProbeIntervalRef = useRef<number | null>(null);
  // LOTE 4.1 — a trilha CLONADA que alimenta o analyser (ver attachRemote).
  // Clone não morre com a original: stop() explícito no teardown.
  const analyserTrackRef = useRef<MediaStreamTrack | null>(null);
  // LOTE 4.1 — o modelo chegou a falar nesta sessão? (output_audio_buffer.started)
  const spokeRef = useRef(false);
  // Transcripts, drain state and server-session metadata belong to LiveRun,
  // so a delayed response cannot consume the next run's queue or session.
  // LOTE 4 — aba escondida: tolerância (ms, do token) e o timer armado.
  const hiddenGraceMsRef = useRef<number>(30_000);
  const hiddenTimerRef = useRef<number | null>(null);

  const setStatus = useCallback((s: MirrorLiveStatus) => {
    statusRef.current = s;
    setStatusState(s);
  }, []);

  const syncRemaining = useCallback((value: number) => {
    remainingRef.current = { value: Math.max(0, value), atMs: Date.now() };
    setSecondsRemaining(Math.max(0, value));
  }, []);

  const clearTimers = useCallback(() => {
    if (tickTimerRef.current !== null) {
      window.clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    }
    if (expiryTimerRef.current !== null) {
      window.clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (disconnectTimerRef.current !== null) {
      window.clearTimeout(disconnectTimerRef.current);
      disconnectTimerRef.current = null;
    }
    if (hiddenTimerRef.current !== null) {
      window.clearTimeout(hiddenTimerRef.current);
      hiddenTimerRef.current = null;
    }
    if (analyserProbeTimerRef.current !== null) {
      window.clearTimeout(analyserProbeTimerRef.current);
      analyserProbeTimerRef.current = null;
    }
    if (analyserProbeIntervalRef.current !== null) {
      window.clearInterval(analyserProbeIntervalRef.current);
      analyserProbeIntervalRef.current = null;
    }
  }, []);

  /**
   * LOTE 4.4 — fecha o AudioContext PRÓPRIO da sessão (e solta a referência).
   * Chamado pelo silenceLocal e pelos retornos antecipados do start(): um
   * contexto criado no gesto e nunca fechado é um consumidor de áudio vivo
   * depois da sessão — e no iOS há um teto de contextos abertos. close() é
   * assíncrono e nunca bloqueia; falha vira aviso.
   */
  const closeAudioContext = useCallback(() => {
    const ctx = audioCtxRef.current;
    audioCtxRef.current = null;
    if (!ctx) return;
    try {
      if (ctx.state !== "closed") {
        ctx.close().catch((e) => console.warn("[useMirrorLive] AudioContext.close() falhou", e));
      }
    } catch (e) {
      console.warn("[useMirrorLive] AudioContext.close() lançou", e);
    }
  }, []);

  /**
   * LOTE 5 — TODO caminho que encerra áudio passa por aqui: fecha o
   * AudioContext próprio e devolve a AVAudioSession ao modo .playback.
   *
   * As duas coisas juntas por invariante, não por conveniência: quem entrou no
   * LIVE flipou a sessão nativa para playAndRecord/.voiceChat, e sair sem
   * desfazer deixa o app inteiro em modo de gravação — TTS abafado no Espelho,
   * no check-in, em tudo. Os retornos antecipados do start() (mic negado,
   * cancelamento, token recusado) contam: lá a sessão já foi flipada e a
   * conversa nunca aconteceu.
   */
  const releaseRunNativeAudio = useCallback((run: LiveRun) => {
    const focus = run.focusLease;
    run.focusLease = null;
    const lease = run.nativeAudioLease;
    run.nativeAudioLease = null;
    // Release is idempotent and token-bound. An older run cannot restore
    // playback while another LIVE owns the native route.
    if (lease) void lease.release().catch(e => console.warn("[useMirrorLive] native audio release failed", e)).finally(() => focus?.release());
    else focus?.release();
  }, []);

  const releaseAudio = useCallback(() => {
    closeAudioContext();
    nativeAudioRef.current = null;
    const run = runRef.current;
    if (run) releaseRunNativeAudio(run);
  }, [closeAudioContext, releaseRunNativeAudio]);

  /** One remote graph at a time, including a failed partial attachment. */
  const clearRemoteAnalyser = useCallback(() => {
    if (analyserProbeTimerRef.current !== null) window.clearTimeout(analyserProbeTimerRef.current);
    if (analyserProbeIntervalRef.current !== null) window.clearInterval(analyserProbeIntervalRef.current);
    analyserProbeTimerRef.current = null;
    analyserProbeIntervalRef.current = null;
    try {
      analyserSrcRef.current?.disconnect();
    } catch (_e) {
      // já desconectado
    }
    analyserSrcRef.current = null;
    try {
      analyserRef.current?.disconnect();
    } catch (_e) {
      // já desconectado
    }
    analyserRef.current = null;
    // LOTE 4.1 — a trilha clonada NÃO morre com a original (pc.close() só
    // encerra a remota): sem este stop() vaza um consumidor de áudio por sessão.
    try {
      analyserTrackRef.current?.stop();
    } catch (_e) {
      // já parada
    }
    analyserTrackRef.current = null;
    setRemoteAnalyser(null);
  }, []);

  /**
   * Silencia a MÍDIA LOCAL: microfone parado, voz remota muda, grafo do
   * analyser desfeito, timers limpos. É o que o usuário vê ao pedir para
   * parar — e acontece ANTES de qualquer await (review Codex P1 do LOTE 3).
   */
  const silenceLocal = useCallback(() => {
    clearTimers();
    stopTracks(micRef.current);
    micRef.current = null;
    clearRemoteAnalyser();
    remoteAudioTrackRef.current = null;
    remoteAttachmentGenerationRef.current += 1;
    // LOTE 4.4 — o contexto PRÓPRIO morre com a sessão, junto com o clone.
    // LOTE 5 — e a sessão nativa volta para .playback no mesmo movimento.
    releaseAudio();
    const el = audioElRef.current;
    if (el) {
      try {
        el.pause();
        (el as HTMLMediaElement & { srcObject: MediaStream | null }).srcObject = null;
      } catch (_e) {
        // elemento já solto
      }
    }
    audioElRef.current = null;
    setMicStream(null);
    setRemoteStream(null);
    setRemoteAnalyser(null);
    setPresence("idle");
  }, [clearTimers, releaseAudio, clearRemoteAnalyser]);

  const cancelToolJobs = useCallback((run: LiveRun) => {
    const tools = run.tools;
    if (!tools) return;
    tools.state = invalidateMirrorLiveCurrentTurn(tools.state);
    for (const job of tools.jobs) {
      job.controller.abort();
      if (job.timer !== null) window.clearTimeout(job.timer);
      job.timer = null;
      job.wake?.();
    }
    tools.jobs.clear();
  }, []);

  /** Closes only this run's transport, including stale startup completions. */
  const closeRunTransport = useCallback((run: LiveRun) => {
    try { run.dc?.close(); } catch (e) { console.warn("[useMirrorLive] dc close", e); }
    if (dcRef.current === run.dc) dcRef.current = null;
    run.dc = null;
    try { run.pc?.close(); } catch (e) { console.warn("[useMirrorLive] pc close", e); }
    if (pcRef.current === run.pc) pcRef.current = null;
    run.pc = null;
  }, []);

  // Stale completion may only dispose its own resources. In particular it
  // must not restore native playback while a newer run owns the audio mode.
  const disposeRun = useCallback((run: LiveRun) => {
    cancelToolJobs(run);
    if (runRef.current === run) silenceLocal();
    releaseRunNativeAudio(run);
    stopTracks(run.mic);
    try {
      if (run.context && run.context.state !== "closed") {
        run.context.close().catch(e => console.warn("[useMirrorLive] stale context close", e));
      }
    } catch (e) { console.warn("[useMirrorLive] stale context close", e); }
    try { run.audio?.pause(); if (run.audio) run.audio.srcObject = null; } catch { /* already detached */ }
    closeRunTransport(run);
  }, [silenceLocal, closeRunTransport, cancelToolJobs, releaseRunNativeAudio]);

  /**
   * Review Codex P1 (LOTE 4.2) — espera (≤ TRANSCRIPT_DRAIN_MS) pelas falas do
   * usuário já comprometidas mas ainda sem transcrição, e por uma resposta
   * do modelo em curso (que é cancelada — o transcript parcial chega no
   * `…output_audio_transcript.done`). null = nada pendente, nada a esperar:
   * o caminho sem transcrição pendente continua 100 % síncrono até o tique.
   */
  const drainTranscripts = useCallback((run: LiveRun): Promise<void> | null => {
    const pendingUser = run.pendingUserItems.size;
    if (pendingUser === 0 && !run.responseActive) return null;
    if (run.responseActive) {
      const dc = run.dc as { send?: (data: string) => void } | null;
      try {
        if (dc && typeof dc.send === "function") dc.send(JSON.stringify({ type: "response.cancel" }));
      } catch (e) {
        console.warn("[useMirrorLive] response.cancel no encerramento", e);
      }
    }
    return new Promise<void>((resolve) => {
      const timer = window.setTimeout(() => {
        run.drainResolve = null;
        console.warn(
          "[useMirrorLive] transcrição pendente não chegou em " + String(TRANSCRIPT_DRAIN_MS) + " ms — encerrando sem ela (falas do usuário sem texto=" +
            String(run.pendingUserItems.size) + ", resposta em curso=" + String(run.responseActive) + ")",
        );
        resolve();
      }, TRANSCRIPT_DRAIN_MS);
      run.drainResolve = () => {
        window.clearTimeout(timer);
        run.drainResolve = null;
        resolve();
      };
    });
  }, []);

  /**
   * Tique ao servidor. O corpo leva só session_id, end, (uma vez) call_id,
   * ended_detail e (LOTE 4.2) as falas transcritas pendentes — TEXTO com
   * item_id. NUNCA duração: o delta é do servidor.
   */
  const sendTick = useCallback(async (run: LiveRun, end: boolean, detail?: LiveEndedDetail): Promise<MirrorLiveTickResponse | null> => {
    if (!end && (!isActiveRun(run) || run.ending)) return null;
    const sid = run.sessionId;
    if (!sid) return null;
    const body: Record<string, unknown> = { session_id: sid };
    if (end) {
      body.end = true;
      // LOTE 4 — por quê (diagnóstico). Só junto do end.
      if (detail) body.ended_detail = detail;
    }
    const includeCallId = run.callId !== null && !run.callIdSent;
    if (includeCallId) body.call_id = run.callId;
    // LOTE 4.2 — a transcrição pega carona. O tique final leva o rabo.
    const batch = run.transcripts.slice(0, TRANSCRIPTS_PER_TICK);
    if (batch.length > 0) body.transcripts = batch;
    const freshToken = run.authority?.token();
    if (freshToken) run.accessToken = freshToken;
    const credential = run.accessToken;
    if (run.authority && !credential) return null;
    const { data, error } = await supabase.functions.invoke(TICK_FN, {
      body, ...(run.authority ? { headers: { Authorization: "Bearer " + credential } } : {}),
    });
    if (error) throw error;
    if (includeCallId) run.callIdSent = true;
    const r = data as MirrorLiveTickResponse;
    // A legacy missing ACK still preserves the old persistence behavior below,
    // but can never authorize a tool. The bridge independently reads the exact row.
    if (run.tools && r?.transcripts_ok === true && r.continue === true) {
      for (const transcript of batch) {
        if (transcript.role === "user" && run.tools.state.transcripts.get(transcript.item_id) === transcript.text)
          run.tools.acknowledged.add(transcript.item_id);
      }
    }
    if (batch.length > 0) {
      if (r && r.transcripts_ok === false) {
        // O servidor não gravou (migration 20260905190000 atrasada?): o lote
        // fica e vai de novo no próximo tique — o item_id impede duplicata.
        console.error("[useMirrorLive] transcrição NÃO gravada pelo servidor (" + String(batch.length) + " falas) — reenvio no próximo tique");
      } else {
        const sent = new Set(batch.map((t) => t.item_id));
        run.transcripts = run.transcripts.filter((t) => !sent.has(t.item_id));
      }
    }
    if (isActiveRun(run) && !run.ending && r && typeof r.seconds_remaining === "number") syncRemaining(r.seconds_remaining);
    return r ?? null;
  }, [syncRemaining, isActiveRun]);

  // The first end must include a call ID returned by an already submitted
  // SDP request: the server cannot attach it after closing the session row.
  // Local media teardown never waits for this best-effort remote cleanup.
  const sendFinalTick = useCallback(async (run: LiveRun, detail?: LiveEndedDetail) => {
    if (run.finalTickPending || (run.serverClosed && run.transcripts.length === 0)) return;
    if (run.handshakeInFlight && !run.callId) {
      run.deferredEnd = { detail };
      return;
    }
    run.deferredEnd = null;
    run.finalTickPending = true;
    try {
      await sendTick(run, true, detail);
      run.serverClosed = true;
    } finally {
      run.finalTickPending = false;
    }
  }, [sendTick]);

  /**
   * Encerramento único. MÍDIA LOCAL PRIMEIRO, síncrono (review Codex P1): o
   * tique final aguarda o hangup na OpenAI, e um servidor lento não pode
   * deixar o microfone aberto nem a voz tocando depois de o usuário pedir
   * para parar. O aviso ao servidor vem depois, com o que já está em refs.
   */
  const finish = useCallback(async (reason: MirrorLiveEndReason, notifyServer: boolean, detail?: LiveEndedDetail, capturedRun?: LiveRun) => {
    const run = capturedRun ?? runRef.current;
    if (!run || run.ending) return;
    run.ending = true;
    cancelToolJobs(run);
    // Invalida qualquer start() ainda em voo (review Codex P1).
    if (runRef.current === run) {
      genRef.current += 1;
      silenceLocal();
      setEndReason(reason);
      setStatus("ended");
    }
    // LOTE 4.2 (review Codex P1): o canal de dados fica aberto SÓ o tempo de
    // a transcrição pendente chegar — sem nada pendente, nem um await.
    const drain = drainTranscripts(run);
    if (drain) await drain;
    closeRunTransport(run);
    // O tique final vai quando o servidor ainda não sabe do fim OU quando há
    // rabo de transcrição (mesmo que o servidor já tenha fechado: end numa
    // sessão fechada é idempotente, e o rabo entra dentro da janela).
    const mustNotify = notifyServer && !run.serverClosed;
    const hasTail = run.transcripts.length > 0;
    if ((mustNotify || hasTail) && run.sessionId) {
      try {
        await sendFinalTick(run, detail);
      } catch (e) {
        // O servidor cobra o rabo no próximo tique de outra sessão ou o
        // fecha como 'expired' — nunca prende o usuário aqui.
        console.error("[useMirrorLive] tique final (end) falhou", e);
      }
    }
  }, [sendFinalTick, silenceLocal, closeRunTransport, drainTranscripts, setStatus, cancelToolJobs]);
  const finishRef = useRef(finish);
  finishRef.current = finish;

  const tick = useCallback(async (run: LiveRun) => {
    if (!isActiveRun(run) || statusRef.current !== "live" || run.ending) return;
    try {
      const r = await sendTick(run, false);
      if (!isActiveRun(run) || run.ending) return;
      tickFailuresRef.current = 0;
      if (r && r.continue === false) {
        // O servidor já fechou (cota / outra aba / expirou): não mandar end.
        run.serverClosed = true;
        await finishRef.current(endReasonFromTick(r.ended_reason), false, undefined, run);
      }
    } catch (e) {
      if (!isActiveRun(run) || run.ending) return;
      tickFailuresRef.current += 1;
      console.error("[useMirrorLive] tique falhou (" + String(tickFailuresRef.current) + "/" + String(TICK_FAILURES_BEFORE_END) + ")", e);
      if (tickFailuresRef.current >= TICK_FAILURES_BEFORE_END) {
        // Cliente honesto falha fechado: sem cobrança confirmada, não segue.
        await finishRef.current("disconnected", true, "tick_failed", run);
      }
    }
  }, [sendTick, isActiveRun]);

  useEffect(() => {
    const authority = opts.authority;
    if (!authority) return;
    return authority.subscribeInvalidation(() => {
      const run = runRef.current;
      if (!run?.authority || run.authority.isCurrent() || run.invalidated) return;
      run.invalidated = true;
      run.ending = true;
      genRef.current += 1;
      // This callback runs under the auth client lock. Media is disposed
      // synchronously; authenticated network cleanup starts after it returns.
      disposeRun(run);
      run.drainResolve?.();
      run.transcripts = [];
      run.pendingUserItems.clear();
      run.responseActive = false;
      setEndReason("client");
      setStatus("ended");
      setSecondsRemaining(null);
      setSessionExpiresAt(null);
      remainingRef.current = null;
      if (run.sessionId && !run.serverClosed) {
        queueMicrotask(() => {
          void sendFinalTick(run, "navigation").catch(e => console.warn("[useMirrorLive] revoked session cleanup failed", e));
        });
      }
    });
  }, [opts.authority, disposeRun, sendFinalTick, setStatus]);

  const ownsToolJob = useCallback((run: LiveRun, job: LiveToolJob) =>
    isActiveRun(run) && !run.ending && !run.serverClosed && statusRef.current === "live"
    && !!run.authority && !!run.tools && !job.controller.signal.aborted
    && isMirrorLiveCurrentTurnCallCurrent(run.tools.state, job.call), [isActiveRun]);

  const executeCurrentTurn = useCallback(async (run: LiveRun, call: MirrorLiveCurrentTurnCall) => {
    const tools = run.tools;
    if (!tools || !run.authority || !run.sessionId) return;
    const job: LiveToolJob = { call, controller: new AbortController(), wake: null, timer: null };
    tools.jobs.add(job);
    const current = () => ownsToolJob(run, job);
    const output = (payload: Record<string, unknown>) => {
      if (!current() || !run.dc || run.dc.readyState !== "open") return;
      // Identity, issuer lease and literal transcript never enter provider output.
      run.dc.send(JSON.stringify({ type: "conversation.item.create",
        item: { type: "function_call_output", call_id: call.callId, output: JSON.stringify(payload) } }));
      if (current()) run.dc.send(JSON.stringify({ type: "response.create" }));
    };
    try {
      const deadline = Date.now() + LIVE_TOOL_TRANSCRIPT_WAIT_MS;
      let transcript: string | null | undefined;
      while (current() && (transcript = tools.state.transcripts.get(call.sourceItemId)) === undefined) {
        const remaining = deadline - Date.now();
        if (remaining <= 0) break;
        await new Promise<void>(resolve => {
          job.wake = resolve;
          job.timer = window.setTimeout(resolve, remaining);
        });
        if (job.timer !== null) window.clearTimeout(job.timer);
        job.timer = null; job.wake = null;
      }
      if (!current()) return;
      transcript = tools.state.transcripts.get(call.sourceItemId);
      if (typeof transcript !== "string") {
        output({ ok: false, error: "transcript_unavailable", response: mirrorChannelCopy(tools.language).failed, executed: false });
        return;
      }
      // Flush the original canonical user item through ordinary tick persistence.
      // At most the existing 200-entry buffer is walked, without synthesizing text.
      for (let attempt = 0; !tools.acknowledged.has(call.sourceItemId) && attempt < 2; attempt++) {
        if (!current() || !run.transcripts.some(item => item.item_id === call.sourceItemId && item.role === "user" && item.text === transcript)) break;
        const tickResult = await sendTick(run, false);
        if (!current()) return;
        if (tickResult?.continue === false) {
          run.serverClosed = true;
          void finishRef.current(endReasonFromTick(tickResult.ended_reason), false, undefined, run);
          return;
        }
        if (tickResult?.transcripts_ok !== true || tickResult.continue !== true) break;
      }
      if (!current()) return;
      if (!tools.acknowledged.has(call.sourceItemId)) {
        output({ ok: false, error: "transcript_unconfirmed", response: mirrorChannelCopy(tools.language).failed, executed: false });
        return;
      }
      const sessionId = await tools.prepareSession();
      if (!current()) return;
      if (!sessionId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(sessionId)) {
        output({ ok: false, error: "session_unavailable", response: mirrorChannelCopy(tools.language).failed, executed: false });
        return;
      }
      // An ambient account replacement cannot lend its JWT to an older command.
      const auth = await supabase.auth.getSession();
      if (!current()) return;
      const identity = auth.error ? null : mirrorAuthIdentity(auth.data.session);
      if (!identity || identity.ownerId !== run.authority.ownerId || identity.authSessionId !== run.authority.authSessionId) {
        output({ ok: false, error: "invalid_session", response: mirrorChannelCopy(tools.language).denied, executed: false });
        return;
      }
      job.timer = window.setTimeout(() => {
        if (current()) output({ ok: false, error: "bridge_timeout", response: mirrorChannelCopy(tools.language).failed, executed: false });
        job.controller.abort();
      }, LIVE_TOOL_REQUEST_TIMEOUT_MS);
      const response = await fetch(supabaseUrl + "/functions/v1/mirror-chat", {
        method: "POST", signal: job.controller.signal,
        headers: { Authorization: "Bearer " + identity.accessToken, "Content-Type": "application/json" },
        body: JSON.stringify({ promptType: "chat", channel: "live", sessionId, language: tools.language,
          live_tool: { lease: tools.lease, source_item_id: call.sourceItemId, call_id: call.callId } }),
      });
      if (!current()) return;
      const outcome = await readMirrorChannelOutcome(response, { expectedSessionId: sessionId });
      if (!current()) return;
      if (outcome.kind === "final") {
        const envelope = outcome.envelope;
        if (envelope.handoff) {
          tools.onHandoff?.({ handoff: envelope.handoff, response: envelope.response, transcript,
            ownerId: run.authority.ownerId, authSessionId: run.authority.authSessionId,
            liveSessionId: run.sessionId, runNonce: tools.runNonce, sessionId,
            sourceItemId: call.sourceItemId, callId: call.callId });
          if (!current()) return;
        }
        output({ ok: true, response: envelope.response.slice(0, 4000), read_only: true, executed: false,
          ...(envelope.handoff ? { handoff: envelope.handoff, executed: false, receipt: null } : {}) });
      } else if (outcome.kind === "handoff") {
        const responseText = mirrorAppHandoffResponse(outcome.handoff.reason, tools.language);
        tools.onHandoff?.({ handoff: outcome.handoff, response: responseText, transcript,
          ownerId: run.authority.ownerId, authSessionId: run.authority.authSessionId,
          liveSessionId: run.sessionId, runNonce: tools.runNonce, sessionId,
          sourceItemId: call.sourceItemId, callId: call.callId });
        if (!current()) return;
        output({ ok: true, response: responseText.slice(0, 4000), read_only: true, handoff: outcome.handoff, executed: false, receipt: null });
      } else {
        const copy = mirrorChannelCopy(tools.language);
        output({ ok: false, error: outcome.code,
          response: outcome.kind === "quota" ? copy.quota : outcome.kind === "locked" ? copy.locked : outcome.kind === "denied" ? copy.denied : copy.failed,
          executed: false });
      }
    } catch (error) {
      if (current()) {
        console.warn("[useMirrorLive] current turn bridge failed", error);
        output({ ok: false, error: "bridge_failed", response: mirrorChannelCopy(tools.language).failed, executed: false });
      }
    } finally {
      if (job.timer !== null) window.clearTimeout(job.timer);
      job.wake = null; tools.jobs.delete(job);
    }
  }, [ownsToolJob, sendTick]);

  const handleEvent = useCallback((run: LiveRun, raw: unknown) => {
    if (!isActiveRun(run)) return;
    let msg: { type?: unknown; error?: unknown; item_id?: unknown; transcript?: unknown; content_index?: unknown } | null = null;
    try {
      msg = typeof raw === "string" ? JSON.parse(raw) : null;
    } catch (_e) {
      return;
    }
    if (!msg) return;
    if (msg.type === "error") {
      console.error("[useMirrorLive] evento error da OpenAI", msg.error ?? msg);
      return;
    }
    if (msg.type === "output_audio_buffer.started") spokeRef.current = true;
    // Review Codex P1 — o que ainda falta chegar antes de fechar o canal:
    // fala do usuário comprometida (server VAD) sem transcrição, e resposta
    // do modelo em curso.
    if (msg.type === "input_audio_buffer.committed" && typeof msg.item_id === "string" && msg.item_id) {
      run.pendingUserItems.add(msg.item_id);
    } else if (
      (msg.type === "conversation.item.input_audio_transcription.completed" || msg.type === "conversation.item.input_audio_transcription.failed") &&
      typeof msg.item_id === "string"
    ) {
      run.pendingUserItems.delete(msg.item_id);
      if (msg.type === "conversation.item.input_audio_transcription.failed") {
        console.warn("[useMirrorLive] transcrição da fala do usuário falhou na OpenAI (item " + msg.item_id.slice(0, 24) + ") — essa fala não será gravada", msg.error ?? "");
      }
    } else if (msg.type === "response.created") {
      run.responseActive = true;
    } else if (msg.type === "response.done") {
      run.responseActive = false;
    }
    if (run.drainResolve && run.pendingUserItems.size === 0 && !run.responseActive) {
      run.drainResolve();
    }
    // LOTE 4.2 — o que foi dito vai para a fila do tique (idempotente por item_id).
    const transcript = transcriptFromEvent(msg);
    if (transcript) {
      const buf = run.transcripts;
      if (!buf.some((t) => t.item_id === transcript.item_id)) {
        buf.push(transcript);
        if (buf.length > TRANSCRIPT_BUFFER_MAX) {
          buf.splice(0, buf.length - TRANSCRIPT_BUFFER_MAX);
          console.warn("[useMirrorLive] " + String(TRANSCRIPT_BUFFER_MAX) + " falas esperando tique — as mais antigas caíram");
        }
      }
    }
    if (run.tools && run.authority && !run.ending && !run.serverClosed && statusRef.current === "live") {
      const transition = advanceMirrorLiveCurrentTurn(run.tools.state, msg, TRANSCRIPT_MAX_CHARS);
      run.tools.state = transition.state;
      for (const job of run.tools.jobs) {
        if (!ownsToolJob(run, job)) job.controller.abort();
        job.wake?.();
      }
      if (transition.call) void executeCurrentTurn(run, transition.call);
    }
    const p = presenceFromEvent(msg.type);
    if (p && !run.ending) setPresence(p);
  }, [isActiveRun, ownsToolJob, executeCurrentTurn]);

  const attachRemote = useCallback((run: LiveRun, stream: MediaStream) => {
    if (!isActiveRun(run) || run.ending) { stopTracks(stream); return; }
    const audioTrack = stream.getAudioTracks()[0];
    if (!audioTrack) return;
    const sameTrack = remoteAudioTrackRef.current === audioTrack;
    // Repeated ontrack may carry another stream wrapper for the same track.
    // Reassigning srcObject restarts the media route; another clone leaks a
    // second analyser consumer. A failed graph may retry without restarting sound.
    if (sameTrack && (analyserRef.current || !audioCtxRef.current)) return;
    clearRemoteAnalyser();
    const el = audioElRef.current;
    if (!sameTrack) {
      remoteAudioTrackRef.current = audioTrack;
      remoteAttachmentGenerationRef.current += 1;
      setRemoteStream(stream);
    }
    const attachment = remoteAttachmentGenerationRef.current;
    if (el && !sameTrack) {
      try {
        (el as HTMLMediaElement & { srcObject: MediaStream | null }).srcObject = stream;
        el.muted = mutedRef.current;
        const p = el.play();
        if (p && typeof (p as Promise<void>).catch === "function") {
          (p as Promise<void>).catch((err) => {
            if (isActiveRun(run) && !run.ending && remoteAttachmentGenerationRef.current === attachment)
              console.warn("[useMirrorLive] autoplay do áudio remoto", err);
          });
        }
      } catch (e) {
        console.warn("[useMirrorLive] anexar áudio remoto", e);
      }
    }
    // Boca do avatar: analyser sobre o stream remoto. createMediaStreamSource
    // não reroteia a saída (ao contrário do createMediaElementSource), então
    // é seguro no iOS. O áudio sai pelo <audio> de qualquer jeito; o grafo
    // abaixo existe SÓ para o analyser ter dado.
    //
    // LOTE 4 — por que o grafo tem regras, cada uma por um silêncio real:
    //   1. (LOTE 4.4) TERMINA NO ANALYSER: source → analyser, e só. O
    //      GainNode(0) → destination do 4.1 era uma segunda rota até a saída
    //      (o <audio> já é a primeira) e é o suspeito dos estalos; o
    //      AnalyserNode processa com a saída solta (automatic pull node).
    //      A sonda de 15 s decide: pico > 0 com falou=true, o ganho era
    //      desnecessário.
    //   2. ESTADO conferido depois do resume(): contexto "suspended" devolve
    //      zeros sem erro nenhum — a boca fechada em silêncio ERA o defeito.
    //   3. SONDA única ~2 s depois: a média do getByteFrequencyData no
    //      console.info. Zero com voz tocando = diagnóstico em uma linha.
    //   4. (LOTE 4.4) o contexto é o PRÓPRIO da sessão (audioCtxRef), criado
    //      no gesto pelo start(); sem ele não há grafo — e grita.
    const ctx = audioCtxRef.current;
    if (!ctx) {
      console.error("[useMirrorLive] sem AudioContext próprio no attach — o LIVE fala pelo <audio>, a boca não vai mexer");
    }
    if (ctx) {
      // LOTE 5 — medidas fora do try: o resumo de 15 s (e o evento do Sentry)
      // precisa delas mesmo se a montagem do grafo falhar no meio.
      let remoteRate: number | null = null;
      let micRate: number | null = null;
      const nativeAudio = nativeAudioRef.current;
      const ctxOutputLatency =
        typeof (ctx as AudioContext & { outputLatency?: number }).outputLatency === "number"
          ? (ctx as AudioContext & { outputLatency?: number }).outputLatency ?? null
          : null;
      try {
        // LOTE 4.1 (05/09/2026) — o analyser lê uma CÓPIA da trilha, não a
        // trilha que o <audio> toca. Medido em produção com o grafo do LOTE 4:
        // ctx=running, grafo fechado no destination, e média=0.0 mesmo assim.
        // No Chromium e no WebKit, a MediaStream remota ligada ao <audio> E ao
        // createMediaStreamSource entrega silêncio ao Web Audio — o elemento
        // consome a trilha. Uma trilha clonada tem consumidor próprio. Quem ler
        // isto depois vai achar o clone estranho e querer desfazer: não desfaça
        // sem rodar a sonda de novo. O clone NÃO morre com a original — stop()
        // no teardown (analyserTrackRef).
        const cloned = audioTrack.clone();
        analyserTrackRef.current = cloned;
        const analyserStream = new MediaStream([cloned]);
        const src = ctx.createMediaStreamSource(analyserStream);
        analyserSrcRef.current = src;
        const an = ctx.createAnalyser();
        analyserRef.current = an;
        an.fftSize = 512;
        src.connect(an);
        setRemoteAnalyser(an);
        // LOTE 4.4 — a SONDA DE TAXAS, uma linha: o contexto (próprio desde
        // este lote) contra o que as trilhas declaram. ctx ≠ trilha remota =
        // resampling contínuo no analyser; mic é a taxa da sessão de áudio do
        // aparelho (no iOS, a mesma da reprodução). baseLatency e state
        // completam o quadro sem experimento manual.
        remoteRate = trackSampleRate(audioTrack);
        micRate = trackSampleRate(micRef.current ? micRef.current.getAudioTracks()[0] : null);
        const rateMismatch = remoteRate !== null && remoteRate !== ctx.sampleRate;
        // LOTE 5 — a QUARTA taxa: a que a AVAudioSession de fato negociou. É a
        // que faltava para o setPreferredSampleRate(48_000) deixar de ser
        // palpite: com contexto, trilha, mic e sessão nativa na mesma linha dá
        // para ver quem está reamostrando quem. `rota` responde se o som saiu
        // pelo alto-falante ou pelo receptor.
        console.info(
          "[useMirrorLive] sonda do áudio (LOTE 4.4): ctx.sampleRate=" + String(ctx.sampleRate) +
            " trilha_remota=" + (remoteRate === null ? "n/a" : String(remoteRate)) +
            " mic=" + (micRate === null ? "n/a" : String(micRate)) +
            " sessao_nativa=" + (nativeAudio?.sampleRate == null ? "n/a" : String(nativeAudio.sampleRate)) +
            " nativa_preferida=" + (nativeAudio?.preferredSampleRate == null ? "n/a" : String(nativeAudio.preferredSampleRate)) +
            " rota=" + (nativeAudio?.outputRoute ?? "n/a") +
            " baseLatency=" + (typeof ctx.baseLatency === "number" ? ctx.baseLatency.toFixed(4) : "n/a") +
            " outputLatency=" + (ctxOutputLatency === null ? "n/a" : ctxOutputLatency.toFixed(4)) +
            " ctx.state=" + String(ctx.state) +
            " contexto=próprio" +
            (rateMismatch ? " — TAXAS DIFERENTES (ctx ≠ trilha remota): resampling contínuo no analyser" : ""),
        );
      } catch (e) {
        clearRemoteAnalyser();
        console.warn("[useMirrorLive] analyser remoto indisponível", e);
      }
      void (async () => {
        try {
          if (ctx.state !== "running") await ctx.resume();
        } catch (e) {
          if (!isActiveRun(run)) return;
          console.error("[useMirrorLive] AudioContext.resume() falhou — a boca não vai mexer", e);
        }
        if (!isActiveRun(run)) return;
        if (ctx.state !== "running") {
          console.error("[useMirrorLive] AudioContext " + String(ctx.state) + " depois do resume() — o analyser devolve zeros e a boca fica fechada");
        }
      })();
      const readMean = (): number | null => {
        if (!isActiveRun(run) || run.ending) return null;
        const an = analyserRef.current;
        if (!an) return null;
        try {
          const buf = new Uint8Array(an.frequencyBinCount);
          an.getByteFrequencyData(buf);
          let sum = 0;
          for (let i = 0; i < buf.length; i++) sum += buf[i];
          return buf.length > 0 ? sum / buf.length : 0;
        } catch (e) {
          console.warn("[useMirrorLive] sonda do analyser falhou", e);
          return null;
        }
      };
      // Primeira leitura aos 2 s (LOTE 4) — compatível com o que já foi lido em produção.
      if (analyserProbeTimerRef.current !== null) window.clearTimeout(analyserProbeTimerRef.current);
      analyserProbeTimerRef.current = window.setTimeout(() => {
        if (!isActiveRun(run) || run.ending) return;
        analyserProbeTimerRef.current = null;
        const mean = readMean();
        if (mean === null) return;
        console.info(
          "[useMirrorLive] sonda do analyser remoto: média=" + mean.toFixed(1) + " (bins=" + String(analyserRef.current?.frequencyBinCount ?? 0) +
            ", ctx=" + String(ctx.state) + ") — zero com voz tocando = boca sem dado",
        );
      }, ANALYSER_PROBE_DELAY_MS);
      // LOTE 4.1 — a janela: amostra por 15 s e resume UMA vez. É este resumo
      // que aceita ou reprova a boca: pico > 0 com falou=true.
      if (analyserProbeIntervalRef.current !== null) window.clearInterval(analyserProbeIntervalRef.current);
      let peak = 0;
      let total = 0;
      let withSignal = 0;
      let samples = 0;
      const maxSamples = Math.floor(ANALYSER_PROBE_WINDOW_MS / ANALYSER_PROBE_SAMPLE_MS);
      analyserProbeIntervalRef.current = window.setInterval(() => {
        if (!isActiveRun(run) || run.ending) return;
        const mean = readMean();
        if (mean !== null) {
          samples += 1;
          total += mean;
          if (mean > peak) peak = mean;
          if (mean > 0) withSignal += 1;
        }
        if (samples >= maxSamples || mean === null) {
          if (analyserProbeIntervalRef.current !== null) window.clearInterval(analyserProbeIntervalRef.current);
          analyserProbeIntervalRef.current = null;
          const avg = samples > 0 ? total / samples : 0;
          console.info(
            "[useMirrorLive] sonda do analyser remoto (" + String(ANALYSER_PROBE_WINDOW_MS / 1000) + " s): pico=" + peak.toFixed(1) +
              " média=" + avg.toFixed(1) + " amostras_com_sinal=" + String(withSignal) + "/" + String(samples) +
              " falou=" + String(spokeRef.current) + " ctx=" + String(ctx.state) +
              " — ACEITE: pico > 0 com falou=true; pico 0 com falou=true = analyser mudo",
          );
          // LOTE 5 — e SAI DO APARELHO. Uma vez por sessão, nível info, pela
          // faixa de analítica do sentryBeforeSend (força info, passa antes da
          // dedupe). Este console.info acima já era breadcrumb desde sempre —
          // e breadcrumb sem evento nunca chegou a lugar nenhum.
          reportLiveAudio({
            ctxSampleRate: typeof ctx.sampleRate === "number" ? ctx.sampleRate : null,
            remoteTrackSampleRate: remoteRate,
            micTrackSampleRate: micRate,
            ctxBaseLatency: typeof ctx.baseLatency === "number" ? ctx.baseLatency : null,
            ctxOutputLatency,
            ctxState: String(ctx.state),
            nativeSampleRate: nativeAudio?.sampleRate ?? null,
            nativePreferredSampleRate: nativeAudio?.preferredSampleRate ?? null,
            nativeIoBufferDuration: nativeAudio?.ioBufferDuration ?? null,
            nativeOutputLatency: nativeAudio?.outputLatency ?? null,
            nativeOutputRoute: nativeAudio?.outputRoute ?? null,
            peak,
            mean: avg,
            samplesWithSignal: withSignal,
            samplesTotal: samples,
            spoke: spokeRef.current === true,
          });
        }
      }, ANALYSER_PROBE_SAMPLE_MS);
    }
  }, [isActiveRun, clearRemoteAnalyser]);

  // (e) aba escondida — a regra num lugar só (review Codex P1 do LOTE 4.1).
  // O microfone segue a visibilidade SEMPRE que o temos, inclusive em
  // requesting_mic/connecting: esconder o app com o handshake em curso deixava
  // o mic aberto (e a janela desarmada) até a PRÓXIMA troca de aba, porque o
  // listener devolvia cedo fora de 'live' e nada reavaliava ao entrar. A janela
  // de tolerância só conta ao vivo. Chamado pelo visibilitychange, ao receber
  // o microfone e ao entrar em 'live'.
  const syncVisibility = useCallback(() => {
    const run = runRef.current;
    if (!run || !isActiveRun(run) || run.ending) return;
    const hidden = document.visibilityState === "hidden";
    const mic = micRef.current;
    if (mic) {
      try {
        mic.getTracks().forEach((t) => {
          t.enabled = !hidden;
        });
      } catch (e) {
        console.warn("[useMirrorLive] mic enabled=" + String(!hidden), e);
      }
    }
    if (!hidden) {
      // Visível (voltou dentro da janela, ou nunca escondeu): microfone de
      // volta, timer cancelado.
      if (hiddenTimerRef.current !== null) {
        window.clearTimeout(hiddenTimerRef.current);
        hiddenTimerRef.current = null;
      }
      return;
    }
    // LOTE 4.1 (decisão do Rafa, 05/09) — MICROFONE SILENCIADO ANTES da
    // janela de tolerância. Microfone aberto com o app minimizado é
    // PRIVACIDADE, não custo — e isto opera na Europa. A COBRANÇA CONTINUA
    // durante a janela, de propósito: a sessão segue aberta e a OpenAI segue
    // faturando; parar de cobrar seria mentir para a cota.
    if (statusRef.current !== "live") return;
    if (hiddenTimerRef.current !== null) return;
    hiddenTimerRef.current = window.setTimeout(() => {
      if (!isActiveRun(run) || run.ending) return;
      hiddenTimerRef.current = null;
      if (statusRef.current === "live" && document.visibilityState === "hidden") {
        void finishRef.current("client", true, "hidden", run);
      }
    }, hiddenGraceMsRef.current);
  }, [isActiveRun]);

  const start = useCallback(async () => {
    const languageProfessor = professorRef.current;
    if (!enabled) return;
    const s = statusRef.current;
    if (s === "requesting_mic" || s === "connecting" || s === "live") return;
    const authority = opts.authority?.acquire() ?? null;
    if (opts.authority && !authority) {
      setEndReason("error");
      setStatus("ended");
      return;
    }
    const prepareToolSession = opts.prepareToolSession, onToolHandoff = opts.onToolHandoff;
    const negotiateTools = !languageProfessor && !!authority && typeof prepareToolSession === "function";
    const previous = runRef.current;
    if (previous) {
      previous.invalidated = true;
      cancelToolJobs(previous);
      previous.drainResolve?.();
      closeRunTransport(previous);
      releaseRunNativeAudio(previous);
    }
    const gen = ++genRef.current;
    const run: LiveRun = {
      generation: gen, authority, invalidated: false, ending: false,
      sessionId: null, accessToken: authority?.originalToken ?? null,
      callId: null, callIdSent: false, serverClosed: false,
      handshakeInFlight: false, deferredEnd: null, finalTickPending: false,
      transcripts: [], pendingUserItems: new Set(), responseActive: false, drainResolve: null,
      mic: null, pc: null, dc: null, context: null, nativeAudioLease: null, focusLease: languageProfessor ? null : appAudioFocus.acquire("live"), audio: null, tools: null,
    };
    runRef.current = run;
    // true quando um finish() rodou depois deste start() começar: o que
    // vier de um await a partir daí é devolvido, nunca instalado.
    const cancelled = () => gen !== genRef.current || !isActiveRun(run);
    tickFailuresRef.current = 0;
    spokeRef.current = false;
    setEndReason(null);
    setSecondsRemaining(null);
    setSessionExpiresAt(null);
    remainingRef.current = null;
    setPresence("idle");

    // Premium Professor checks authoritative access before touching audio or microphone.
    // The preflight cannot create a key/session or consume quota. General LIVE stays unchanged.
    if (languageProfessor) {
      setStatus("connecting");
      try {
        const capability = await supabase.functions.invoke(TOKEN_FN, { method: "GET" });
        if (cancelled()) return;
        if (capability.error || capability.data?.language_professor_protocol !== 1
          || capability.data?.language_professor_premium_protocol !== 1) {
          disposeRun(run); run.invalidated = true;
          setEndReason("disabled"); setStatus("ended"); return;
        }
        const session = await supabase.auth.getSession();
        if (cancelled()) return;
        if (session.error || !session.data.session?.access_token
          || (run.authority && !run.authority.acceptsSession(session.data.session))) {
          disposeRun(run); run.invalidated = true;
          setEndReason("error"); setStatus("ended"); return;
        }
        run.accessToken = run.authority ? run.authority.token() : session.data.session.access_token;
        const access = await supabase.functions.invoke(TOKEN_FN, {
          body: { purpose: "language_professor", language_professor: languageProfessor, check_access: true },
          headers: { Authorization: "Bearer " + run.accessToken },
        });
        if (cancelled()) return;
        if (access.error || access.data?.allowed !== true || access.data?.language_professor_premium_protocol !== 1) {
          const premiumRequired = await isProfessorPremiumRequiredError(access.error);
          if (cancelled()) return;
          disposeRun(run); run.invalidated = true;
          setEndReason(premiumRequired ? "premium_required" : "error"); setStatus("ended"); return;
        }
      } catch {
        if (cancelled()) return;
        disposeRun(run); run.invalidated = true;
        setEndReason("error"); setStatus("ended"); return;
      }
      if (cancelled()) return;
      run.focusLease = appAudioFocus.acquire("live");
    }

    // (a) 🔴 MICROFONE PRIMEIRO. Negou = nada foi cobrado.
    setStatus("requesting_mic");
    // LOTE 5 — a AVAudioSession ANTES de abrir o microfone, no MESMO gesto,
    // exatamente como o useVoiceInputWhisper já faz com setRecordMode: a
    // categoria tem de estar posta quando o WKWebView pega a trilha, não
    // depois. .voiceChat liga o AEC do sistema e a rota duplex; sem isso o
    // LIVE roda com o que o WKWebView escolher. No-op fora do iOS.
    // Guarda a medida (taxa efetiva, rota) para a sonda — ver reportLiveAudio.
    // The lease claims the global native route before awaiting the bridge.
    // Delayed STT/TTS restores cannot change its category during this run.
    const nativeAudioLease = acquireVoiceChatAudioSession();
    run.nativeAudioLease = nativeAudioLease;
    const nativeAudio = await nativeAudioLease.ready;
    if (cancelled()) { releaseRunNativeAudio(run); return; }
    nativeAudioRef.current = nativeAudio;
    let mic: MediaStream;
    try {
      // Conversa em duplex: cancelamento de eco LIGADO (ao contrário da
      // gravação do Whisper) — senão o modelo se ouve pelo alto-falante.
      mic = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (e) {
      // LOTE 5 — a sessão nativa JÁ foi flipada para .voiceChat acima. Sem
      // microfone não há conversa: desfaz antes dos dois retornos deste catch,
      // senão o app fica em playAndRecord por causa de uma permissão negada.
      if (cancelled()) return;
      releaseAudio();
      console.warn("[useMirrorLive] microfone negado/indisponível", e);
      setEndReason("mic_denied");
      setStatus("ended");
      return;
    }
    if (cancelled()) {
      // A superfície fechou enquanto a permissão estava aberta: nada foi
      // cobrado ainda — devolve o microfone e para aqui.
      stopTracks(mic);
      if (runRef.current === run) releaseAudio();
      return;
    }
    micRef.current = mic;
    run.mic = mic;
    setMicStream(mic);
    // Permissão concedida com o app já escondido: silencia agora, não na
    // próxima troca de aba.
    syncVisibility();

    // Ainda dentro do gesto do usuário: cria o AudioContext PRÓPRIO desta
    // sessão (LOTE 4.4 — não o compartilhado do desbloqueio iOS) e o elemento
    // que vai tocar a voz remota. A taxa vem da trilha do microfone quando ela
    // a declara: no iOS é a taxa da sessão de áudio, a mesma da reprodução.
    // LOTE 4: o estado é CONFERIDO depois do resume() — "suspended" aqui é a
    // boca fechada sem erro, e falha silenciosa é o defeito, não o contexto.
    closeAudioContext();
    const liveCtx = createLiveAudioContext(trackSampleRate(mic.getAudioTracks()[0]));
    audioCtxRef.current = liveCtx;
    run.context = liveCtx;
    if (liveCtx && liveCtx.state !== "running") {
      liveCtx.resume().then(
        () => {
          if (!isActiveRun(run)) return;
          if (liveCtx.state !== "running") console.error("[useMirrorLive] AudioContext " + String(liveCtx.state) + " depois do resume() no gesto");
        },
        (e) => { if (isActiveRun(run)) console.error("[useMirrorLive] AudioContext.resume() no gesto falhou", e); },
      );
    }
    const el = document.createElement("audio");
    el.autoplay = true;
    el.setAttribute("playsinline", "true");
    el.muted = mutedRef.current;
    audioElRef.current = el;
    run.audio = el;

    // (b) a chave — depois do microfone, nunca antes.
    setStatus("connecting");
    try {
      const { data: sess, error: sessionError } = await supabase.auth.getSession();
      if (cancelled()) return;
      if (run.authority && (sessionError || !run.authority.acceptsSession(sess?.session))) {
        disposeRun(run); run.invalidated = true;
        setEndReason("error"); setStatus("ended");
        return;
      }
      run.accessToken = run.authority ? run.authority.token() : sess?.session?.access_token ?? null;
    } catch (_e) {
      if (cancelled()) return;
      if (run.authority) {
        disposeRun(run); run.invalidated = true;
        setEndReason("error"); setStatus("ended");
        return;
      }
      run.accessToken = null;
    }
    if (cancelled()) return;
    // General LIVE sends only UI language; Professor adds catalogue identifiers.
    // Duration, quota and prompt instructions remain server-owned.
    const toolLanguage = languageRef.current;
    const { data, error } = await supabase.functions.invoke(TOKEN_FN, {
      body: { language: languageRef.current, ...(languageProfessor ? { purpose: "language_professor", language_professor: languageProfessor } : {}) },
      ...(run.authority ? { headers: { Authorization: "Bearer " + run.accessToken, ...(negotiateTools ? { "x-mirror-live-tools": "1" } : {}) } } : {}),
    });
    if (cancelled()) {
      // Cancelado com o token em voo. Se a chave saiu, a sessão JÁ foi
      // pré-cobrada: encerra no servidor (end) sem nunca abrir o handshake.
      stopTracks(mic);
      disposeRun(run);
      if (!error && isTokenResponse(data)) {
        run.sessionId = data.session_id;
        try {
          await sendFinalTick(run, "navigation");
        } catch (e) {
          console.error("[useMirrorLive] end da sessão cancelada falhou", e);
        }
      }
      return;
    }
    if (error) {
      const pe = await parsePaymentErrorFromInvokeError(error);
      if (cancelled()) return;
      stopTracks(micRef.current);
      micRef.current = null;
      setMicStream(null);
      releaseAudio();
      if (languageProfessor && await isProfessorPremiumRequiredError(error)) {
        if (cancelled()) return;
        setEndReason("premium_required");
      } else if (pe) {
        onQuotaRef.current?.(pe);
        setEndReason("no_quota");
      } else if (await isLiveDisabledError(error)) {
        if (cancelled()) return;
        // LOTE 4 — kill switch: desligado por config, nada cobrado. Copy
        // própria; não é erro do usuário nem nosso.
        console.warn("[useMirrorLive] LIVE desligado por config (503 live_disabled)");
        setEndReason("disabled");
      } else {
        if (cancelled()) return;
        console.error("[useMirrorLive] mirror-live-token falhou", error);
        setEndReason("error");
      }
      setStatus("ended");
      return;
    }
    if (!isTokenResponse(data)) {
      console.error("[useMirrorLive] resposta do token com shape inesperado", data && typeof data === "object" ? Object.keys(data as object) : typeof data);
      stopTracks(micRef.current);
      micRef.current = null;
      setMicStream(null);
      releaseAudio();
      setEndReason("error");
      setStatus("ended");
      return;
    }
    const tok = data;
    run.sessionId = tok.session_id;
    if (languageProfessor && tok.language_professor_protocol !== 1) {
      disposeRun(run);
      try { await sendFinalTick(run, "navigation"); }
      catch (error) { console.warn("[useMirrorLive] Professor protocol mismatch cleanup failed", error); }
      if (!cancelled()) { setEndReason("disabled"); setStatus("ended"); }
      return;
    }
    if (negotiateTools && prepareToolSession && typeof tok.live_tool_lease === "string" && tok.live_tool_lease.length > 0
      && tok.live_tool_lease.length <= 8192 && typeof tok.live_tool_run_nonce === "string"
      && /^[A-Za-z0-9_-]{1,200}$/.test(tok.live_tool_run_nonce)) {
      run.tools = { lease: tok.live_tool_lease, runNonce: tok.live_tool_run_nonce, language: toolLanguage,
        prepareSession: prepareToolSession, onHandoff: onToolHandoff, state: createMirrorLiveCurrentTurnState(),
        acknowledged: new Set(), jobs: new Set() };
    }
    syncRemaining(tok.seconds_remaining);
    // LOTE 4 — tolerância da aba escondida: do token; function antiga (sem o
    // campo) = tick_seconds, o default pedido.
    const grace = typeof tok.hidden_grace_seconds === "number" && Number.isFinite(tok.hidden_grace_seconds)
      ? Math.max(0, tok.hidden_grace_seconds)
      : Math.max(0, tok.tick_seconds);
    hiddenGraceMsRef.current = grace * 1000;

    // (b') WebRTC direto com a OpenAI, com a chave EFÊMERA (ek_…).
    try {
      const pc = new RTCPeerConnection();
      pcRef.current = pc;
      run.pc = pc;
      pc.ontrack = (ev) => {
        const stream = ev.streams && ev.streams[0] ? ev.streams[0] : new MediaStream([ev.track]);
        attachRemote(run, stream);
      };
      pc.onconnectionstatechange = () => {
        const cs = pc.connectionState;
        if (!isActiveRun(run) || run.ending) return;
        if (statusRef.current !== "live") return;
        if (cs === "failed" || cs === "closed") {
          void finishRef.current("disconnected", true, "disconnected", run);
          return;
        }
        if (cs === "disconnected") {
          // Transitório em troca de rede / ICE restart (review Codex P2):
          // espera DISCONNECT_GRACE_MS antes de encerrar; volta = cancela.
          if (disconnectTimerRef.current === null) {
            disconnectTimerRef.current = window.setTimeout(() => {
              if (!isActiveRun(run) || run.ending) return;
              disconnectTimerRef.current = null;
              if (statusRef.current === "live") void finishRef.current("disconnected", true, "disconnected", run);
            }, DISCONNECT_GRACE_MS);
          }
          return;
        }
        if (cs === "connected" && disconnectTimerRef.current !== null) {
          window.clearTimeout(disconnectTimerRef.current);
          disconnectTimerRef.current = null;
        }
      };
      const dc = pc.createDataChannel(OAI_EVENTS_CHANNEL);
      dcRef.current = dc;
      run.dc = dc;
      if (languageProfessor) dc.onopen = () => {
        dc.onopen = null;
        if (!isActiveRun(run) || run.ending) return;
        // The learner explicitly started this session. Ask for the server-owned
        // teacher greeting once; no client prompt or fabricated learner turn.
        dc.send(JSON.stringify({ type: "response.create" }));
      };
      dc.onmessage = (ev) => handleEvent(run, ev.data);
      mic.getTracks().forEach((t) => pc.addTrack(t, mic));
      const offer = await pc.createOffer();
      if (cancelled()) { closeRunTransport(run); return; }
      await pc.setLocalDescription(offer);
      if (cancelled()) { closeRunTransport(run); return; }
      run.handshakeInFlight = true;
      let res: Response;
      try {
        res = await fetch(OPENAI_REALTIME_CALLS_URL, {
          method: "POST",
          headers: {
            "Authorization": "Bearer " + tok.client_secret,
            "Content-Type": "application/sdp",
          },
          body: offer.sdp ?? "",
        });
      } finally {
        run.handshakeInFlight = false;
      }
      run.callId = extractCallId(res.headers.get("Location"));
      if (cancelled()) {
        closeRunTransport(run);
        try { await sendFinalTick(run, run.deferredEnd?.detail ?? "navigation"); }
        catch (e) { console.warn("[useMirrorLive] late handshake cleanup failed", e); }
        return;
      }
      if (!res.ok) throw await readOpenAiHttpError(res, "realtime");
      // (c) o call_id vem no Location. Sem ele o servidor não tem como
      // derrubar esta chamada — precisa ser visível (§11), não mudo.
      const callId = run.callId;
      if (!callId) {
        console.error("[useMirrorLive] handshake sem call_id no Location — hangup pelo servidor indisponível nesta sessão (CORS não expõe o header?)");
      }
      run.callId = callId;
      const answer = await res.text();
      if (cancelled()) { closeRunTransport(run); return; }
      await pc.setRemoteDescription({ type: "answer", sdp: answer });
      if (cancelled()) { closeRunTransport(run); return; }
    } catch (e) {
      if (cancelled()) {
        if (run.deferredEnd) {
          try { await sendFinalTick(run, run.deferredEnd.detail); }
          catch (cleanupError) { console.warn("[useMirrorLive] failed handshake cleanup failed", cleanupError); }
        }
        return;
      }
      console.error("[useMirrorLive] handshake WebRTC falhou", e,
        e instanceof OpenAiHttpError ? e.diagnostics() : { stage: "realtime_handshake" });
      await finishRef.current("error", true, "handshake_error", run);
      return;
    }
    if (cancelled()) return; // idem: sessão já encerrada pelo finish()

    // (d) ao vivo. Primeiro tique JÁ (delta 0 dentro do bloco pré-pago, sem
    // cobrança): é ele que leva o call_id ao servidor o quanto antes.
    setStatus("live");
    // O handshake pode ter terminado com a aba já escondida: arma a janela de
    // tolerância (e mantém o mic silenciado) sem esperar outro visibilitychange.
    syncVisibility();
    void tick(run);
    const everyMs = Math.max(5, tok.tick_seconds) * 1000;
    tickTimerRef.current = window.setInterval(() => void tick(run), everyMs);

    // (f) fim REAL da sessão na OpenAI — só quando o servidor o conhece (hoje
    // quase sempre null) e só com folga. Nunca a validade da chave. O piso em
    // zero que ficava aqui matou o LIVE em 2 s (ver sessionExpiryDelayMs):
    // prazo no passado = bug de servidor → grita e a sessão SEGUE.
    // LOTE 4: o RELATIVO (session_ttl_seconds) vale mais que o ISO — o ISO
    // só entra quando a function antiga não manda o relativo.
    const hasTtl = tok.session_ttl_seconds !== undefined;
    const expiryMs = hasTtl ? sessionTtlDelayMs(tok.session_ttl_seconds) : sessionExpiryDelayMs(tok.session_expires_at, Date.now());
    // Exposto à UI: com o relativo, o ISO é DERIVADO dele (o session_expires_at
    // deprecado pode discordar do relógio do aparelho — é o ponto do LOTE 4).
    setSessionExpiresAt(
      expiryMs === null ? null : hasTtl ? new Date(Date.now() + expiryMs).toISOString() : tok.session_expires_at,
    );
    if (expiryMs !== null) {
      expiryTimerRef.current = window.setTimeout(() => {
        if (isActiveRun(run) && !run.ending) void finishRef.current("expired", true, "timer", run);
      }, expiryMs);
    }
  }, [enabled, opts.authority, opts.prepareToolSession, opts.onToolHandoff, isActiveRun, setStatus, syncRemaining, attachRemote, handleEvent, tick, syncVisibility, closeAudioContext, releaseAudio, closeRunTransport, disposeRun, sendFinalTick, cancelToolJobs, releaseRunNativeAudio]);

  const end = useCallback(async () => {
    if (statusRef.current === "idle" || statusRef.current === "ended") return;
    await finishRef.current("client", true, "button");
  }, []);

  const setMuted = useCallback((muted: boolean) => {
    mutedRef.current = muted;
    setIsMutedState(muted);
    const el = audioElRef.current;
    if (el) el.muted = muted;
  }, []);

  // (g) contagem local entre tiques — o número que o usuário vê cair.
  useEffect(() => {
    if (status !== "live") return;
    const run = runRef.current;
    if (!run) return;
    const id = window.setInterval(() => {
      if (!isActiveRun(run) || run.ending) return;
      const r = remainingRef.current;
      if (!r) return;
      const elapsed = Math.floor((Date.now() - r.atMs) / 1000);
      setSecondsRemaining(Math.max(0, r.value - elapsed));
    }, 1000);
    return () => window.clearInterval(id);
  }, [status, isActiveRun]);

  // (e) enabled → false (troca de superfície /mirror/live → /mirror com o
  // MESMO componente montado): encerra com end:true.
  useEffect(() => {
    if (enabled) return;
    const s = statusRef.current;
    if (s === "live" || s === "connecting" || s === "requesting_mic") {
      void finishRef.current("client", true, "navigation");
    }
  }, [enabled]);

  // (e) aba escondida / página saindo. LOTE 4: esconder NÃO encerra na hora —
  // arma hidden_grace_seconds (do token); voltar a visible dentro da janela
  // cancela; vencer a janela encerra como 'hidden'. Dez trocas de app no
  // celular custavam dez pré-cobranças. A regra vive em syncVisibility (acima).
  // No pagehide não há tempo para o invoke — fetch keepalive com o JWT em
  // cache é o que chega.
  useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => syncVisibility();
    const onPageHide = () => {
      const run = runRef.current;
      if (!run || !isActiveRun(run)) return;
      const sid = run.sessionId;
      const token = run.authority?.token() ?? run.accessToken;
      if (!sid || run.serverClosed || statusRef.current !== "live" || !token) return;
      run.serverClosed = true;
      const pending = run.transcripts.slice(0, TRANSCRIPTS_PER_TICK);
      const tail = fitTranscriptsInBytes(pending, KEEPALIVE_BODY_MAX_BYTES);
      if (tail.length < run.transcripts.length) {
        console.error(
          "[useMirrorLive] pagehide: " + String(run.transcripts.length - tail.length) +
            " falas ficaram de fora do keepalive (teto de " + String(KEEPALIVE_BODY_MAX_BYTES) + " bytes)",
        );
      }
      try {
        void fetch(supabaseUrl + "/functions/v1/" + TICK_FN, {
          method: "POST",
          keepalive: true,
          headers: { "Authorization": "Bearer " + token, "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sid,
            end: true,
            ended_detail: "navigation",
            // LOTE 4.2 — o rabo do que foi dito vai junto; é a última chance.
            // Review Codex P2: cabe no teto do keepalive, senão o end se perde.
            ...(tail.length > 0 ? { transcripts: tail } : {}),
          }),
        }).catch(() => {});
      } catch (_e) {
        // sem rede no unload: o varredor fecha como 'expired' em ≤ ~3,5 min
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
    };
  }, [enabled, syncVisibility, isActiveRun]);

  // (e) unmount: end:true (detail 'navigation' — saiu da tela).
  useEffect(() => {
    return () => {
      const s = statusRef.current;
      if (s === "live" || s === "connecting" || s === "requesting_mic") {
        void finishRef.current("client", true, "navigation");
      }
    };
  }, []);

  return {
    status,
    presence,
    endReason,
    secondsRemaining,
    sessionExpiresAt,
    micStream,
    remoteStream,
    remoteAnalyser,
    isMuted,
    start,
    end,
    setMuted,
  };
}
